import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { DEFAULT_SITE, deepMerge } from "./defaultContent";
import { cache } from "./cache";

const SiteCtx = createContext(null);
const CACHE_KEY = "site";

async function fetchWithRetry(path, attempts = 3) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      const r = await api.get(path);
      return r.data;
    } catch (e) {
      lastErr = e;
      await new Promise((res) => setTimeout(res, 250 * (i + 1) * (i + 1)));
    }
  }
  throw lastErr;
}

function buildInitial() {
  if (typeof window !== "undefined" && window.__ATC_BOOTSTRAP__?.site) {
    return deepMerge(DEFAULT_SITE, window.__ATC_BOOTSTRAP__.site);
  }
  const cached = cache.get(CACHE_KEY);
  if (cached) return deepMerge(DEFAULT_SITE, cached);
  return DEFAULT_SITE;
}

export function SiteProvider({ children }) {
  const [site, setSite] = useState(buildInitial);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchWithRetry("/site");
      if (data && typeof data === "object") {
        cache.set(CACHE_KEY, data);
        setSite(deepMerge(DEFAULT_SITE, data));
      }
    } catch {
      // Keep cached or bundled defaults
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return <SiteCtx.Provider value={{ site, refresh }}>{children}</SiteCtx.Provider>;
}

/** Returns the merged site settings (always non-null, with defaults). */
export function useSite() {
  const ctx = useContext(SiteCtx);
  return ctx?.site || DEFAULT_SITE;
}
